import React, { useState, useEffect, useMemo, useRef, Fragment } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Search, X } from "lucide-react";

/* ============ 🔎 Where Is My Part? — live part traceability ============
   Embedded inside the Factory Assistant. READ-ONLY presentation layer:
   every number is the value an EXISTING endpoint already computed —
   /inventory/part-flow (the Part Flow engine), /raw-material/masters,
   /rework/history, /jobwork/snapshot+entries, /planning/dispatch-entries.
   Nothing is recalculated beyond the same sums the Balance Traceability
   drawer already displays, and nothing is ever written. A source the user
   cannot access (RBAC) or that fails shows "Data unavailable" for its
   card — never a guessed number, never a fake 0. */

const n = (x) => Number(x || 0).toLocaleString("en-IN");
const CAT = { blanks: "Blanks", press_shop: "Press", machine_shop: "Machine", job_work: "Job Work", inspection: "Inspection", bop: "BOP" };
const catColor = (c) =>
  ({
    blanks: "bg-cyan-100 text-cyan-700",
    press_shop: "bg-blue-100 text-blue-700",
    machine_shop: "bg-purple-100 text-purple-700",
    job_work: "bg-amber-100 text-amber-700",
    inspection: "bg-emerald-100 text-emerald-700",
    bop: "bg-slate-200 text-slate-700",
  }[c] || "bg-slate-100 text-slate-700");

const DEPT = { press_shop: "Press Shop", machine_shop: "Machine Shop", final_inspection: "Final Inspection" };
const ERR = "__ERR__";                       // a fetch that failed => "Data unavailable"
const maxDate = (dates) => {
  const ok = dates.filter(Boolean).map(String);
  return ok.length ? ok.sort()[ok.length - 1] : null;
};

const Arrow = () => (
  <div className="text-center text-muted-foreground leading-none select-none py-0.5" aria-hidden="true">↓</div>
);

/* Small labeled line inside a stage card */
const Line = ({ k, v, tone }) => (
  <div className="flex justify-between gap-3 text-[11px]">
    <span className="text-muted-foreground">{k}</span>
    <span className={`tabular-nums font-medium ${tone || ""}`}>{v}</span>
  </div>
);

export default function WhereIsMyPartTrace({ unitId }) {
  const [parts, setParts] = useState(null);           // unit part list (search source)
  const [query, setQuery] = useState("");
  const [dropOpen, setDropOpen] = useState(false);
  const boxRef = useRef(null);

  const [partId, setPartId] = useState("");
  const [flow, setFlow] = useState(null);             // /inventory/part-flow — the core
  const [flowErr, setFlowErr] = useState(false);
  const [rm, setRm] = useState(null);                 // /raw-material/masters/{id} | ERR
  const [rework, setRework] = useState(null);         // /rework/history?part_id | ERR
  const [jwRows, setJwRows] = useState(null);         // /jobwork/snapshot rows for part | ERR
  const [jwEntries, setJwEntries] = useState(null);   // /jobwork/entries for part | ERR
  const [dispatches, setDispatches] = useState(null); // /planning/dispatch-entries?part_id | ERR

  /* part list for the search box */
  useEffect(() => {
    if (!unitId) return;
    let dead = false;
    api.get(`/parts?unit_id=${unitId}`)
      .then((r) => { if (!dead) setParts(r.data || []); })
      .catch(() => { if (!dead) setParts(ERR); });
    return () => { dead = true; };
  }, [unitId]);

  /* close search dropdown on outside click */
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* everything about the selected part — parallel, each source independent */
  useEffect(() => {
    if (!unitId || !partId) return;
    let dead = false;
    setFlow(null); setFlowErr(false); setRm(null); setRework(null);
    setJwRows(null); setJwEntries(null); setDispatches(null);

    api.get(`/inventory/part-flow?unit_id=${unitId}&part_id=${partId}`)
      .then((r) => { if (!dead) setFlow(r.data); })
      .catch(() => { if (!dead) setFlowErr(true); });
    api.get(`/raw-material/masters/${partId}`)
      .then((r) => { if (!dead) setRm(r.data || ERR); })
      .catch(() => { if (!dead) setRm(ERR); });
    api.get(`/rework/history?unit_id=${unitId}&part_id=${partId}`)
      .then((r) => { if (!dead) setRework(r.data || ERR); })
      .catch(() => { if (!dead) setRework(ERR); });
    api.get(`/jobwork/snapshot?unit_id=${unitId}`)
      .then((r) => {
        if (dead) return;
        const rows = Array.isArray(r.data) ? r.data : r.data?.rows || [];
        setJwRows(rows.filter((x) => x.part_id === partId));
      })
      .catch(() => { if (!dead) setJwRows(ERR); });
    api.get(`/jobwork/entries?unit_id=${unitId}`)
      .then((r) => { if (!dead) setJwEntries((r.data || []).filter((e) => e.part_id === partId)); })
      .catch(() => { if (!dead) setJwEntries(ERR); });
    api.get(`/planning/dispatch-entries?unit_id=${unitId}&part_id=${partId}`)
      .then((r) => { if (!dead) setDispatches(r.data || []); })
      .catch(() => { if (!dead) setDispatches(ERR); });

    return () => { dead = true; };
  }, [unitId, partId]);

  const pick = (p) => {
    setPartId(p.id);
    setQuery(`${p.part_no || ""} — ${p.name || ""}`);
    setDropOpen(false);
  };

  const matches = useMemo(() => {
    if (!Array.isArray(parts)) return [];
    const q = query.trim().toLowerCase();
    if (!q) return parts.slice(0, 20);
    return parts
      .filter((p) => `${p.part_no || ""} ${p.name || ""} ${p.customer_name || ""}`.toLowerCase().includes(q))
      .slice(0, 20);
  }, [parts, query]);

  /* ---- derived figures — job-work-aware display model (matches Part Flow):
     an op feeding a job-work op counts its factory-waiting count; the supplier
     side counts supplier_remaining. The exact engine balance is kept alongside
     for the RM-coverage note. ---- */
  const stages = flow?.stages || [];
  const fg = flow?.fg || null;
  const dHeld = (x) => (x.jw_flow_closing != null ? Number(x.jw_flow_closing) : Number(x.stock_held));
  const dSup = (x) => (x.supplier_remaining != null ? Number(x.supplier_remaining) : Number(x.at_supplier || 0));
  const inProcess = stages.reduce((s, x) => s + (x.stock_held != null && dHeld(x) > 0 ? dHeld(x) : 0), 0);
  const discrepancy = stages.reduce((s, x) => s + (x.stock_held != null && dHeld(x) < 0 ? dHeld(x) : 0), 0);
  const atPlater = stages.reduce((s, x) => s + (x.at_supplier != null ? dSup(x) : 0), 0);
  const fgStock = fg ? Number(fg.stock || 0) : 0;
  const total = inProcess + discrepancy + atPlater + fgStock;
  const engineTotal = stages.reduce((s, x) => s + Number(x.stock_held || 0) + Number(x.at_supplier || 0), 0) + fgStock;
  const reworkBal = flow?.rework_pool ? Number(flow.rework_pool.balance) : null;

  /* last movement — only from sources that actually carry dates */
  const lastMovement = maxDate([
    ...(Array.isArray(dispatches) ? dispatches.map((d) => d.date) : []),
    ...(Array.isArray(jwEntries) ? jwEntries.map((e) => e.date) : []),
    ...(rework && rework !== ERR ? (rework.in_rows || []).map((r) => r.date) : []),
    ...(rework && rework !== ERR ? (rework.rows || []).map((r) => r.date) : []),
  ]);

  /* rework "rejected at" — the same quality-sheet attribution the Rework drawer uses */
  const rejectedAt = useMemo(() => {
    if (!rework || rework === ERR) return null;
    const agg = {};
    for (const r of rework.in_rows || []) {
      const key = DEPT[r.department] || r.department || r.operation_name || "Origin not recorded";
      agg[key] = (agg[key] || 0) + Number(r.qty || 0);
    }
    return Object.entries(agg).sort((a, b) => b[1] - a[1]);
  }, [rework]);
  const locRecords = rework && rework !== ERR ? rework.locations || [] : null;
  const locAssigned = Array.isArray(locRecords) ? locRecords.reduce((s, r) => s + Number(r.qty || 0), 0) : 0;

  const jwLastDate = (supplierId) =>
    Array.isArray(jwEntries)
      ? maxDate(jwEntries.filter((e) => e.supplier_id === supplierId).map((e) => e.date))
      : null;

  const stageStatus = (s, isLast) => {
    if (s.stock_held == null) return isLast ? "📦" : "⚪";
    if (dHeld(s) < 0) return "🔴";
    if (s.category === "job_work" && dSup(s) > 0) return "🟠";
    if (dHeld(s) > 0) return "🔵";
    return "🟢";
  };

  return (
    <div className="space-y-2" data-testid="wimp-panel">
      {/* ---------------- search ---------------- */}
      <div ref={boxRef} className="relative" data-testid="wimp-search">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setDropOpen(true); }}
          onFocus={() => setDropOpen(true)}
          placeholder="Search Part Number or name…"
          className="pl-9 pr-8 h-8 text-xs"
          data-testid="wimp-search-input"
        />
        {query && (
          <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => { setQuery(""); setDropOpen(true); }} aria-label="Clear">
            <X className="w-4 h-4" />
          </button>
        )}
        {dropOpen && (
          <div className="absolute z-30 mt-1 w-full rounded-xl border border-border/60 bg-card shadow-xl max-h-60 overflow-y-auto">
            {parts === null ? (
              <p className="p-3 text-xs text-muted-foreground">Loading parts…</p>
            ) : parts === ERR ? (
              <p className="p-3 text-xs text-muted-foreground">Data unavailable</p>
            ) : matches.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">No matching part.</p>
            ) : (
              matches.map((p) => (
                <button key={p.id} type="button"
                        className="w-full text-left px-3 py-2 hover:bg-muted/60 border-b border-border/30 last:border-0"
                        onClick={() => pick(p)} data-testid={`wimp-pick-${p.part_no}`}>
                  <p className="text-xs font-semibold">{p.part_no} <span className="font-normal">— {p.name}</span></p>
                  {p.customer_name && <p className="text-[10px] text-muted-foreground">{p.customer_name}</p>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {!partId ? (
        <p className="text-xs text-muted-foreground">Search and select a part to trace its pieces.</p>
      ) : flowErr ? (
        <p className="text-xs text-muted-foreground">Data unavailable — the part flow for this part could not be loaded.</p>
      ) : !flow ? (
        <p className="text-xs text-muted-foreground">Tracing…</p>
      ) : (
        <div className="space-y-1.5">
          {/* ---------------- header card ---------------- */}
          <div className="rounded-xl border border-border/60 bg-card p-3" data-testid="wimp-header">
            <p className="text-sm font-bold">{flow.part?.part_no}</p>
            <p className="text-xs text-muted-foreground">{flow.part?.name}</p>
            <div className="mt-1.5 space-y-0.5 text-xs">
              <p>Current Total: <b className="tabular-nums">{n(total)} pcs</b>
                <span className="text-[10px] text-muted-foreground ml-1">(as on Inventory, {flow.as_of})</span></p>
              <p>Last movement: <b>{lastMovement || "Data unavailable"}</b></p>
            </div>
            {reworkBal ? (
              <p className="text-[11px] text-muted-foreground mt-1">
                + {n(reworkBal)} pcs held in rework (outside this balance, as on the Rework page).
              </p>
            ) : null}
          </div>

          {/* ---------------- RM ---------------- */}
          <div className="rounded-xl border border-border/60 bg-card p-3" data-testid="wimp-rm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">RM <span className="ml-1 rounded px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-700">Raw Material</span></span>
              <span className="text-xs font-bold tabular-nums">
                {rm === null ? "…" : rm === ERR ? "Data unavailable"
                  : !rm.is_configured ? "Not applicable"
                  : `${n(rm.balance_kg)} kg`}
              </span>
            </div>
            {rm && rm !== ERR && rm.is_configured && (
              <div className="mt-1.5 space-y-0.5">
                {Number(rm.gross_weight_kg) > 0 && (
                  <Line k="≈ pieces (balance ÷ gross wt.)" v={`${n(Math.floor(Number(rm.balance_kg || 0) / Number(rm.gross_weight_kg)))} pcs`} />
                )}
                {rm.grade && <Line k="Grade" v={rm.grade} />}
                <Line k="Location" v="Location not recorded" tone="text-muted-foreground font-normal" />
              </div>
            )}
            {rm && rm !== ERR && !rm.is_configured && (
              <p className="text-[11px] text-muted-foreground mt-1">No RM master configured for this part.</p>
            )}
          </div>
          <Arrow />

          {/* ---------------- operation stages (the Part Flow engine's own rows) ---------------- */}
          {stages.map((s, i) => {
            const next = stages[i + 1];
            const isLast = i === stages.length - 1;
            const good = (Number(s.produced) || 0) + (Number(s.rework_in) || 0) - (Number(s.rejected) || 0);
            const shownHeld = s.stock_held == null ? null : dHeld(s);
            const negative = shownHeld != null && shownHeld < 0;
            return (
              <Fragment key={s.operation_id}>
                {i > 0 && <Arrow />}
                <details
                  className={`rounded-xl border p-3 ${negative ? "border-red-300 bg-red-50/60 dark:border-red-900 dark:bg-red-950/20" : "border-border/60 bg-card"}`}
                  data-testid={`wimp-stage-${s.operation_name}`}
                >
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">
                        {stageStatus(s, isLast)} {s.operation_name}
                        <span className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-normal ${catColor(s.category)}`}>
                          {CAT[s.category] || s.category}
                        </span>
                      </span>
                      <span className={`text-xs font-bold tabular-nums whitespace-nowrap ${shownHeld == null ? "text-muted-foreground" : negative ? "text-red-600" : ""}`}>
                        {shownHeld == null ? "→ FG" : `${n(shownHeld)} pcs`}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                      {Number(s.rejected) > 0 && <span>🔴 Rejected {n(s.rejected)}</span>}
                      {Number(s.rework_in) > 0 && <span>🔧 Rework back {n(s.rework_in)}</span>}
                      {s.category === "job_work" && s.at_supplier != null && <span>🏭 At supplier {n(dSup(s))}</span>}
                      <span>📍 Location not recorded</span>
                      <span>⏱ {s.baseline_as_of ? `physical count ${s.baseline_as_of}` : "Data unavailable"}</span>
                    </div>
                  </summary>
                  <div className="mt-2 pt-2 border-t border-border/40 space-y-0.5">
                    <Line k="Opening" v={n(s.opening)} />
                    <Line k="Made" v={n(s.produced)} />
                    {Number(s.rework_in) > 0 && <Line k="Rework returned in" v={n(s.rework_in)} />}
                    <Line k="Rejected" v={s.rejected ? n(s.rejected) : "—"} tone={Number(s.rejected) > 0 ? "text-red-600" : ""} />
                    <Line k="Good output" v={n(good)} />
                    {s.category === "job_work" && (
                      <>
                        <Line k="Jobwork sent" v={s.sent == null ? "Data unavailable" : n(s.sent)} />
                        <Line k="Jobwork received" v={s.received == null ? "Data unavailable" : n(s.received)} />
                        {s.supplier_opening != null && <Line k="Supplier opening (internal)" v={n(s.supplier_opening)} />}
                        {Number(s.jw_replating_in) > 0 && <Line k="Re-sent for replating (from store)" v={n(s.jw_replating_in)} />}
                        <Line k="Currently at supplier" v={s.at_supplier == null ? "Data unavailable" : `${n(dSup(s))} pcs`} tone="text-amber-600" />
                      </>
                    )}
                    <Line k={next ? `Consumed by ${next.operation_name}` : "Consumed"} v={next && next.produced != null ? n(next.produced) : "—"} />
                    <Line k="Balance here now" v={shownHeld == null ? "→ FG (final operation)" : `${n(shownHeld)} pcs`} tone={negative ? "text-red-600" : ""} />
                    {s.jw_flow_closing != null && next && (
                      <Line k={`Waiting to send to ${next.operation_name}`} v={`${n(s.jw_flow_closing)} pcs (factory) — rest is with the supplier`} tone="text-muted-foreground font-normal" />
                    )}
                    <Line k="Physical count" v={s.baseline_as_of ? `${s.baseline_qty != null ? `${n(s.baseline_qty)} pcs on ` : ""}${s.baseline_as_of}` : "No count recorded"} />
                    <Line k="Location / bin" v="Location not recorded" tone="text-muted-foreground font-normal" />
                    {negative && (
                      <p className="text-[11px] text-red-600 pt-1">
                        ⚠️ Discrepancy detected — this stage's recorded output exceeds the material available.
                        The figure is the existing system value; verify the physical count and entries.
                      </p>
                    )}
                  </div>
                </details>
              </Fragment>
            );
          })}

          {/* ---------------- jobwork (currently outside) ---------------- */}
          {(jwRows === ERR || (Array.isArray(jwRows) && jwRows.length > 0)) && (
            <>
              <Arrow />
              <div className="rounded-xl border border-amber-300 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 p-3" data-testid="wimp-jobwork">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">🏭 Jobwork</span>
                  <span className="text-xs font-bold tabular-nums text-amber-700">
                    {jwRows === ERR ? "Data unavailable" : `${n(jwRows.reduce((s, r) => s + Number(r.closing || 0), 0))} pcs outside`}
                  </span>
                </div>
                {Array.isArray(jwRows) && jwRows.map((r) => (
                  <div key={`${r.supplier_id}-${r.operation_id}`} className="mt-2 pt-2 border-t border-border/40 space-y-0.5">
                    <p className="text-[11px] font-medium">{r.supplier_name} <span className="text-[10px] text-muted-foreground">· {r.operation_name}{r.job_type_name ? ` · ${r.job_type_name}` : ""}</span></p>
                    <Line k="Sent" v={n(r.sent)} />
                    <Line k="Received" v={n(r.received)} />
                    <Line k="Currently outside" v={`${n(r.closing)} pcs`} tone="text-amber-700" />
                    <Line k="Last movement" v={jwEntries === ERR ? "Data unavailable" : jwLastDate(r.supplier_id) || "Data unavailable"} />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ---------------- rework ---------------- */}
          {(reworkBal != null && reworkBal !== 0) || rework === ERR ? (
            <>
              <Arrow />
              <div className="rounded-xl border border-orange-300 dark:border-orange-900 bg-orange-50/60 dark:bg-orange-950/20 p-3" data-testid="wimp-rework">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">🔧 Rework</span>
                  <span className="text-xs font-bold tabular-nums text-orange-700">
                    {reworkBal != null ? `${n(reworkBal)} pcs` : "Data unavailable"}
                  </span>
                </div>
                {flow.rework_pool && (
                  <div className="mt-1 space-y-0.5">
                    <Line k="Sent to rework" v={n(flow.rework_pool.in)} />
                    <Line k="Processed out (scrap / recovered)" v={n(flow.rework_pool.out)} />
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-border/40">
                  <p className="text-[11px] font-semibold mb-0.5">Rejected at (from quality sheets)</p>
                  {rework === null ? (
                    <p className="text-[11px] text-muted-foreground">Loading…</p>
                  ) : rework === ERR ? (
                    <p className="text-[11px] text-muted-foreground">Data unavailable</p>
                  ) : rejectedAt && rejectedAt.length ? (
                    rejectedAt.map(([shop, qty]) => <Line key={shop} k={shop} v={`${n(qty)} pcs`} />)
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Origin not recorded</p>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t border-border/40">
                  <p className="text-[11px] font-semibold mb-0.5">Currently located at</p>
                  {rework === ERR ? (
                    <p className="text-[11px] text-muted-foreground">Data unavailable</p>
                  ) : Array.isArray(locRecords) && locRecords.length > 0 ? (
                    <>
                      {locRecords.map((r) => (
                        <Line key={r.id} k={`${r.location}${r.bin ? ` / ${r.bin}` : ""}${r.origin_operation_name ? ` (from ${r.origin_operation_name})` : ""}`} v={`${n(r.qty)} pcs`} />
                      ))}
                      {reworkBal != null && reworkBal - locAssigned > 0 && (
                        <Line k="Location not recorded" v={`${n(reworkBal - locAssigned)} pcs`} tone="text-muted-foreground font-normal" />
                      )}
                    </>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Location not recorded</p>
                  )}
                </div>
              </div>
            </>
          ) : null}

          {/* ---------------- FG ---------------- */}
          <Arrow />
          <div className="rounded-xl border border-emerald-300 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 p-3" data-testid="wimp-fg">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">📦 Finished Goods</span>
              <span className="text-xs font-bold tabular-nums text-emerald-700">
                {fg ? `${n(fg.stock)} pcs` : "Not applicable"}
              </span>
            </div>
            {fg ? (
              fg.opening_month != null ? (
                <div className="mt-1 space-y-0.5"
                     title={`Count anchor: ${n(fg.opening)} pcs counted on ${fg.opening_as_of}. Since then: +${n(fg.produced_in)} received, −${n(fg.dispatches)} dispatched (all months).`}>
                  <Line k={`FG at month start (${fg.month}-01)`} v={n(fg.opening_month)} />
                  <Line k="+ Received this month" v={n(fg.produced_in_month)} />
                  <Line k="− Dispatched this month" v={n(fg.dispatches_month)} />
                  <Line k="= Current FG" v={`${n(fg.stock)} pcs`} tone="text-emerald-700" />
                </div>
              ) : (
                <div className="mt-1 space-y-0.5">
                  <Line k={`FG opening (as of ${fg.opening_as_of})`} v={n(fg.opening)} />
                  <Line k="+ Received from final operation" v={n(fg.produced_in)} />
                  <Line k="− Dispatched" v={n(fg.dispatches)} />
                  <Line k="= Current FG" v={`${n(fg.stock)} pcs`} tone="text-emerald-700" />
                </div>
              )
            ) : (
              <p className="text-[11px] text-muted-foreground mt-1">Not FG-tracked — no FG opening has been set for this part.</p>
            )}
          </div>

          {/* ---------------- dispatch ---------------- */}
          <Arrow />
          <div className="rounded-xl border border-border/60 bg-card p-3" data-testid="wimp-dispatch">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">🚚 Dispatch</span>
              <span className="text-xs font-bold tabular-nums">
                {fg ? `${n(fg.dispatches_month ?? fg.dispatches)} pcs` : dispatches === ERR ? "Data unavailable" : Array.isArray(dispatches) ? `${n(dispatches.reduce((s, d) => s + Number(d.qty || 0), 0))} pcs` : "…"}
              </span>
            </div>
            {fg && (fg.dispatches_month != null
              ? <p className="text-[10px] text-muted-foreground mt-0.5">Dispatched THIS MONTH — matches the FG card. Total since the FG count ({fg.opening_as_of}): {n(fg.dispatches)} pcs.</p>
              : <p className="text-[10px] text-muted-foreground mt-0.5">Dispatched since FG opening ({fg.opening_as_of}) — same figure as the FG card.</p>)}
            {dispatches === ERR ? (
              <p className="text-[11px] text-muted-foreground mt-1">Dispatch entry details: Data unavailable</p>
            ) : Array.isArray(dispatches) && dispatches.length > 0 ? (
              <div className="mt-2 pt-2 border-t border-border/40 space-y-0.5">
                <Line k="Last dispatch" v={dispatches[0].date} />
                {dispatches.slice(0, 5).map((d) => (
                  <Line key={d.id} k={`${d.date}${d.remarks ? ` · ${d.remarks}` : ""}`} v={`${n(d.qty)} pcs`} />
                ))}
                {dispatches.length > 5 && (
                  <p className="text-[10px] text-muted-foreground">{dispatches.length - 5} earlier entries — see FG Planning for the full list.</p>
                )}
              </div>
            ) : Array.isArray(dispatches) && dispatches.length === 0 ? (
              <p className="text-[11px] text-muted-foreground mt-1">No dispatch entries recorded for this part.</p>
            ) : null}
          </div>

          {/* ---------------- reconciliation ---------------- */}
          <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 mt-2" data-testid="wimp-reconciliation">
            <p className="text-xs font-semibold mb-1.5">Where are the pieces now?</p>
            <div className="space-y-0.5">
              <Line k="🔵 In process (stage bins)" v={`${n(inProcess)} pcs`} />
              <Line k="🏭 Jobwork (at supplier)" v={`${n(atPlater)} pcs`} />
              <Line k="📦 Finished Goods" v={`${n(fgStock)} pcs`} />
              {discrepancy !== 0 && (
                <Line k="⚠️ Discrepancy detected (negative stages)" v={`${n(discrepancy)} pcs`} tone="text-red-600" />
              )}
              <div className="flex justify-between border-t border-border/40 pt-1 mt-1 text-[11px] font-semibold">
                <span>Total (system Balance, as on Inventory)</span>
                <span className="tabular-nums">{n(total)} pcs</span>
              </div>
              {engineTotal !== total && (
                <Line k="Exact engine balance (drives RM coverage)" v={`${n(engineTotal)} pcs`} tone="text-muted-foreground font-normal" />
              )}
              {reworkBal != null && reworkBal !== 0 && (
                <Line k="🔧 In rework (held outside the balance)" v={`${n(reworkBal)} pcs`} tone="text-orange-700" />
              )}
              {fg && <Line k="🚚 Dispatched (already gone, since FG opening)" v={`${n(fg.dispatches)} pcs`} tone="text-muted-foreground font-normal" />}
            </div>
            {discrepancy !== 0 && (
              <p className="text-[11px] text-red-600 mt-2">
                ⚠️ Discrepancy detected — shown exactly as the existing Inventory calculates it. This page never adjusts numbers.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
